<template>
    <div class="coupon-card">
        <div class="coupon-left">
            <div class="coupon-code">{{ (promo.code || '').toUpperCase() }}</div>
            <div class="coupon-title">{{ serviceName || 'Promozione' }}</div>

            <div class="coupon-details">
                <template v-if="variations.length">
                    <span v-for="v in variations" :key="v" class="variation-tag">{{ v }}</span>
                </template>
                <template v-else>
                    <span class="variation-tag">Tutti gli abbonamenti</span>
                </template>
            </div>

            <div class="coupon-validity">
                <span v-if="promo.startDate || promo.endDate">
                    Valido fino: {{ formatDate(promo.endDate) }}
                </span>
                <span v-else>Valido sempre</span>
            </div>

            <div v-if="promo.firstOrder" class="coupon-badge">Solo nuovi tesserati</div>
            <div v-else-if="promo.existingMembersOnly" class="coupon-badge">Riservata ai tesserati</div>
            <div v-else-if="promo.maxUsesPerUser === 1" class="coupon-badge">Una volta per utente</div>

            <!-- La sezione dice dove si entra: qui serve dire su quale listino è calcolato il prezzo -->
            <div v-if="pricingCenterNote" class="coupon-badge coupon-badge--listino">{{ pricingCenterNote }}</div>

            <div v-if="remainingSeatsText" class="coupon-seats">{{ remainingSeatsText }}</div>

            <div v-if="freeDaysLines.length" class="coupon-freedays">
                <span v-for="(line, idx) in freeDaysLines" :key="idx" class="freeday-tag">{{ line }}</span>
            </div>

            <button class="coupon-apply-btn" @click="$emit('apply', { code: promo.code, productId })">
                Applica coupon →
            </button>
        </div>

        <div class="coupon-right">
            <!-- Con i prezzi noti il focus è il prezzo finale; lo sconto resta come indicazione secondaria -->
            <template v-if="pricing">
                <div v-if="pricing.fromMany" class="coupon-price-prefix">a partire da</div>
                <div class="coupon-price-original">{{ formatCurrency(pricing.original) }}</div>
                <div class="coupon-price-final">{{ formatCurrency(pricing.final) }}</div>
                <div class="coupon-price-meta">
                    <span class="coupon-discount-pill">{{ discountLabel }}</span>
                </div>
                <div v-if="!pricing.fromMany && pricing.variation" class="coupon-price-variation">
                    {{ pricing.variation }}
                </div>
            </template>

            <!-- Senza listino disponibile resta la sola entità dello sconto -->
            <template v-else>
                <div class="coupon-discount-value">{{ discountLabel }}</div>
                <div class="coupon-discount-label">SCONTO</div>
            </template>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PromoCouponCard',
    props: {
        promo: { type: Object, required: true },
        /** Mappa productId → servizio di /api/open/services (contiene serviceId e name) */
        servicesMap: { type: Object, default: () => ({}) },
        /** Mappa serviceId → dettaglio di /api/open/services/{id} (contiene periodsAndCosts) */
        serviceDetails: { type: Object, default: () => ({}) },
        /** Centri pubblici, inclusi gli override di prezzo per variazione. */
        centers: { type: Array, default: () => [] },
    },
    emits: ['apply'],
    computed: {
        productId() {
            const p = this.promo || {};
            const variationsKeys = Object.keys(p.variations || {});
            if (variationsKeys.length) return variationsKeys[0];
            const productIds = p?.filters?.productIds;
            if (Array.isArray(productIds) && productIds.length) return productIds[0];
            const freeDaysKeys = Object.keys(p.freeDaysPerProduct || {});
            if (freeDaysKeys.length) return freeDaysKeys[0];
            const freeDaysVariationKeys = Object.keys(p.freeDaysPerProductVariation || {});
            if (freeDaysVariationKeys.length) return freeDaysVariationKeys[0];
            return null;
        },
        serviceName() {
            return this.servicesMap[this.productId]?.name || null;
        },
        variations() {
            const list = (this.promo?.variations || {})[this.productId];
            return Array.isArray(list) ? list : [];
        },
        centerPriceOverrides() {
            const ids = this.promo?.filters?.centerIds;
            if (!Array.isArray(ids) || ids.length !== 1) return null;
            const centerId = String(ids[0]);
            const center = (this.centers || []).find(c => String(c?.id || '') === centerId);
            const overrides = center?.variationPriceOverrides;
            return overrides && typeof overrides === 'object' ? overrides : null;
        },
        /**
         * Con l'accesso esteso il centro del filtro non limita nulla: fissa solo il listino su cui
         * il prezzo è calcolato. Senza dirlo, la card mostrerebbe il prezzo di una sede sotto la
         * sezione «Entrambe le sedi» e sembrerebbe uno sbaglio.
         */
        pricingCenterNote() {
            if (this.promo?.accessPolicy !== 'FOLLOW_PAID_LIST') return '';
            const ids = this.promo?.filters?.centerIds;
            if (!Array.isArray(ids) || ids.length !== 1) return '';
            const center = (this.centers || []).find(c => String(c?.id || '') === String(ids[0]));
            const label = (center?.city || center?.name || '').trim();
            return label ? `Prezzo su listino ${label}` : '';
        },
        discountLabel() {
            const amount = this.promo?.discountAmount || 0;
            if (this.promo?.percentage) return `-${String(amount).replace('.', ',')}%`;
            return `-${this.formatCurrency(amount)}`;
        },
        /**
         * Prezzo di listino e prezzo scontato della variazione più conveniente fra quelle
         * coperte dal coupon. Restituisce null quando il listino non è disponibile,
         * così la card ricade sulla visualizzazione del solo sconto.
         */
        pricing() {
            const service = this.servicesMap[this.productId];
            const detail = service && this.serviceDetails[service.serviceId];
            const periods = detail?.periodsAndCosts;
            if (!Array.isArray(periods) || !periods.length) return null;

            const wanted = this.variations;
            const rows = periods
                .filter(p => p?.first?.active !== false && typeof p?.second === 'number' && p.second > 0)
                .filter(p => !wanted.length || wanted.includes(p.first?.name))
                .map(p => ({
                    variation: p.first?.name || '',
                    original: this.centerPriceOverrides?.[p.first?.name] ?? p.second,
                    final: this.applyDiscount(this.centerPriceOverrides?.[p.first?.name] ?? p.second),
                }))
                .filter(r => r.final !== null && r.final < r.original);

            if (!rows.length) return null;

            const cheapest = rows.reduce((min, r) => (r.final < min.final ? r : min), rows[0]);
            return { ...cheapest, fromMany: rows.length > 1 };
        },
        remainingSeatsText() {
            const p = this.promo;
            if (!p || p.showRemainingSeats !== true) return '';
            if (typeof p.usageLimit !== 'number' || typeof p.currentUsage !== 'number') return '';
            const remaining = Math.max(0, p.usageLimit - p.currentUsage);
            if (remaining === 0) return 'Posti esauriti';
            if (remaining === 1) return '1 posto rimanente';
            return `${remaining} posti rimanenti`;
        },
        freeDaysLines() {
            try {
                const promo = this.promo;
                const productId = this.productId;
                if (!promo || !productId) return [];
                const lines = [];
                const byProduct = (promo.freeDaysPerProduct || {})[productId];
                const byVariationMap = (promo.freeDaysPerProductVariation || {})[productId];

                if (byVariationMap && typeof byVariationMap === 'object') {
                    const entries = Object.entries(byVariationMap).filter(([, v]) => typeof v === 'number' && v > 0);
                    if (entries.length) {
                        const wildcard = entries.find(([k]) => k === '*');
                        entries
                            .filter(([k]) => k !== '*')
                            .forEach(([variationKey, days]) => {
                                lines.push(`+${days} ${days === 1 ? 'giorno gratuito' : 'giorni gratuiti'} con ${variationKey}`);
                            });
                        if (wildcard) {
                            const wd = wildcard[1];
                            lines.push(`+${wd} ${wd === 1 ? 'giorno gratuito' : 'giorni gratuiti'} con altre variazioni`);
                        }
                        return lines;
                    }
                }

                if (typeof byProduct === 'number' && byProduct > 0) {
                    lines.push(`+${byProduct} ${byProduct === 1 ? 'giorno gratuito' : 'giorni gratuiti'} con tutte le variazioni`);
                }
                return lines;
            } catch (e) {
                return [];
            }
        },
    },
    methods: {
        applyDiscount(price) {
            const amount = this.promo?.discountAmount;
            if (typeof amount !== 'number' || amount <= 0) return null;
            const value = this.promo?.percentage ? price * (1 - amount / 100) : price - amount;
            if (!Number.isFinite(value) || value < 0) return null;
            return Math.round(value * 100) / 100;
        },
        formatCurrency(v) {
            try {
                return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);
            } catch {
                return `${v} €`;
            }
        },
        formatDate(ts) {
            if (!ts) return '-';
            try {
                return new Date(ts).toLocaleDateString('it-IT');
            } catch {
                return '-';
            }
        },
    },
};
</script>

<style scoped>
.coupon-card {
    display: flex;
    background: hsl(235 26% 11%);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    overflow: hidden;
    min-height: 180px;
    height: 100%;
    transition: border-color 0.25s ease;
}

.coupon-card:hover {
    border-color: hsl(358 72% 55% / 0.45);
}

.coupon-left {
    flex: 2;
    padding: 22px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.coupon-right {
    flex: 1;
    min-width: 150px;
    background: rgba(255, 255, 255, 0.03);
    border-left: 1px solid rgba(255, 255, 255, 0.1);
    color: #fff;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 20px 16px;
}

.coupon-code {
    align-self: flex-start;
    font-size: 0.95rem;
    font-weight: 700;
    color: hsl(358 72% 62%);
    text-transform: uppercase;
    letter-spacing: 2px;
    margin-bottom: 12px;
    padding: 6px 12px;
    border-radius: 999px;
    background: hsl(358 72% 55% / 0.12);
    border: 1px solid hsl(358 72% 55% / 0.35);
}

.coupon-title {
    font-family: 'Oswald', sans-serif;
    font-size: 1.15rem;
    font-weight: 600;
    color: #fff;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    margin-bottom: 10px;
}

.coupon-details {
    margin-bottom: 12px;
}

.variation-tag {
    display: inline-block;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.75);
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.75rem;
    margin-right: 6px;
    margin-bottom: 6px;
}

.coupon-validity {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.4);
    margin-bottom: 10px;
}

.coupon-badge {
    display: inline-block;
    align-self: flex-start;
    background: hsl(358 72% 55% / 0.15);
    color: hsl(358 72% 66%);
    padding: 5px 12px;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 600;
    margin-bottom: 10px;
}

.coupon-badge--listino {
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.75);
    font-weight: 500;
}

.coupon-seats {
    display: inline-block;
    align-self: flex-start;
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.75);
    padding: 5px 12px;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 600;
    margin-bottom: 10px;
}

.coupon-freedays {
    margin-bottom: 12px;
}

.freeday-tag {
    display: inline-block;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.7);
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 0.72rem;
    margin-right: 6px;
    margin-bottom: 6px;
}

.coupon-apply-btn {
    margin-top: auto;
    align-self: flex-start;
    background: hsl(358 72% 55%);
    color: #fff;
    border: none;
    padding: 11px 22px;
    border-radius: 999px;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    cursor: pointer;
    transition: opacity 0.2s ease;
}

.coupon-apply-btn:hover {
    opacity: 0.9;
}

/* Blocco prezzo */
.coupon-price-prefix {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: rgba(255, 255, 255, 0.4);
    margin-bottom: 4px;
}

.coupon-price-original {
    font-size: 0.95rem;
    color: rgba(255, 255, 255, 0.4);
    text-decoration: line-through;
    margin-bottom: 2px;
}

.coupon-price-final {
    font-family: 'Oswald', sans-serif;
    font-size: 2.3rem;
    font-weight: 700;
    line-height: 1.1;
    color: hsl(358 72% 60%);
}

.coupon-price-meta {
    margin-top: 8px;
}

.coupon-discount-pill {
    display: inline-block;
    background: hsl(358 72% 55% / 0.15);
    color: hsl(358 72% 66%);
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 600;
}

.coupon-price-variation {
    margin-top: 8px;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: rgba(255, 255, 255, 0.45);
}

/* Fallback senza listino */
.coupon-discount-value {
    font-family: 'Oswald', sans-serif;
    font-size: 2.1rem;
    font-weight: 700;
    line-height: 1.1;
    color: hsl(358 72% 60%);
}

.coupon-discount-label {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: rgba(255, 255, 255, 0.45);
    margin-top: 4px;
}

@media (max-width: 575.98px) {
    .coupon-card {
        flex-direction: column;
    }

    .coupon-right {
        border-left: none;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        min-width: 0;
        padding: 18px;
    }
}
</style>
